import {AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {Ordine} from '../../_models/Ordine';
import {FilterItem} from '../../_models/FilterItem';
import {OrganizzazioniService} from '../../_services/organizzazioni.service';
import {LocalService} from '../../_services/local.service';
import {SaveMessage} from '../../_helpers/save-message';
import {Subscription} from 'rxjs';
import {skip} from 'rxjs/operators';
import {RisorseService} from '../../_services/risorse.service';
import {LavorazioniService} from '../../_services/lavorazioni.service';
import {AccountOrganizzazione} from '../../_models/AccountOrganizzazione';
import {HubMonitorService} from '../../_services/hub-monitor.service';
import {matchesFilterItems} from '../../_helpers/filter-item-matcher';
import {AutoLoadTriggerObserver} from '../../_helpers/auto-load-trigger-observer';

@Component({
  selector: 'app-ordini-locali-list',
  templateUrl: './ordini-locali-list.component.html',
  styleUrls: ['./ordini-locali-list.component.scss'],
  standalone: false
})
export class OrdiniLocaliListComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() updateEnable: boolean;
  @Input() flagTerminate: boolean;
  @Input() sortKey: string;
  @Input() sortDirection: boolean;
  @Input() isPersonal: boolean;
  // Filtri di questa tab, posseduti dal padre (OrdiniLocaliComponent) e passati qui in modo
  // scoped: non più letti da LocalService.localFilters (singleton condiviso con l'altra tab).
  @Input() localFilters: FilterItem[] = [];
  @Output() totalQtaChange = new EventEmitter<number>();
  @Output() totalCountChange = new EventEmitter<number>();
  @Output() ordineSended = new EventEmitter<Ordine>();
  loading = false;
  loadMore = false;
  ordinis: Ordine[] = [];
  subscription: Subscription;
  page = 0;
  count = 20;
  filtroOrdini = 0;
  // searchText, i cambi di account e il refresh manuale possono innescare readOrdiniInviati()
  // in sovrapposizione (es. più click su "aggiorna"): senza un guard la risposta che arriva
  // per ultima "vince" anche se non è la più recente, facendo sfarfallare ordinis/totalCount
  // tra valori vecchi e nuovi in modo casuale. requestToken scarta le risposte non più attuali.
  private requestToken = 0;
  private pendingOrdineDeletes: Ordine[] = [];
  private autoLoadTriggerElement?: ElementRef<HTMLElement>;
  private readonly autoLoadTriggerObserver = new AutoLoadTriggerObserver(this.ngZone, () => this.autoLoadMore());

  constructor(
    private risorseService: RisorseService,
    private lavorazioniService: LavorazioniService,
    private organizzazioniService: OrganizzazioniService,
    private localService: LocalService,
    private saveMessage: SaveMessage,
    private ngZone: NgZone,
    private hubMonitorService: HubMonitorService) { }

  @ViewChild('autoLoadTrigger')
  set autoLoadTrigger(element: ElementRef<HTMLElement> | undefined) {
    this.autoLoadTriggerElement = element;
    this.observeAutoLoadTrigger();
  }

  ngOnInit() {
    // Inizializzazione sincrona di filtroOrdini prima del primo caricamento
    const currentAccountOrg = this.organizzazioniService.currentAccountSettingsSubject.getValue();
    if (currentAccountOrg) {
      this.filtroOrdini = currentAccountOrg.tipoFiltroOrdini;
    }

    // searchText gestisce il caricamento iniziale (BehaviorSubject emette subito)
    this.subscription = this.localService.searchText.subscribe((_value: string) => void this.readOrdiniInviati(false));
    // skip(1): filtroOrdini già inizializzato sopra, evita il doppio caricamento su init
    this.subscription.add(this.organizzazioniService
      .currentAccountSettingsSubject
      .pipe(skip(1))
      .subscribe((_accountOrg: AccountOrganizzazione) => {
        if (_accountOrg) {
          this.filtroOrdini = _accountOrg.tipoFiltroOrdini;
        }
        void this.readOrdiniInviati(false);
      }));
    this.subscription.add(this.localService.lastOrdineUpdated.subscribe((_updOrdine: Ordine) => {
      if (_updOrdine != null && _updOrdine.flgTerminato !== this.flagTerminate) {
        this.excludeOrdine(_updOrdine);
      } else if (_updOrdine != null && this.ordinis
        .findIndex((_ord: Ordine) => _ord.idDocumento === _updOrdine.idDocumento && _ord.idRiga === _updOrdine.idRiga &&
          _ord.idRigaLavorazione === _updOrdine.idRigaLavorazione) < 0) {
        if (!this.shouldIncludeUpdatedOrdine(_updOrdine)) {
          return;
        }
        this.ordinis.unshift(_updOrdine);
        this.totalQtaChange.emit(this.ordinis.length);
      }
    }));
    this.subscription.add(this.localService.lastOrdineDeleted.subscribe((_delOrdine: Ordine) => {
      if (_delOrdine != null) {
        this.excludeOrdine(_delOrdine);
      }
    }));
    this.subscription.add(this.localService.pendingOrdineDeletes.subscribe((_pending: Ordine[]) => {
      this.pendingOrdineDeletes = _pending;
      this.applyPendingOrdineDeletes();
    }));
    this.subscription.add(this.localService.handleRefresh.subscribe(() => void this.readOrdiniInviati(false)));
  }

  ngAfterViewInit(): void {
    this.observeAutoLoadTrigger();
  }

  autoLoadMore(): void {
    if (this.loading || !this.loadMore) {
      return;
    }

    void this.readOrdiniInviati(true);
  }

  async readOrdiniInviati(add: boolean) {
    const token = ++this.requestToken;
    const search = this.localService.searchText.getValue().toString();
    this.loadMore = true;
    if (!add) {
      this.ordinis = [];
      this.page = 0;
    } else {
      this.page += 1;
    }
    this.totalQtaChange.emit(0);
    this.loading = true;

    try {
      const _result = await this.localService.getOrdiniInviatiFiltered(this.organizzazioniService.organizzazioneId,
        this.flagTerminate, search, this.page, this.count, this.sortKey, this.sortDirection,
        this.localFilters, this.isPersonal);

      // Nel frattempo è partita una richiesta più recente: questa risposta è superata, va ignorata.
      if (token !== this.requestToken) {
        return;
      }

      this.totalCountChange.emit(_result.totalCount);
      this.sumQta();
      if (_result.totalCount === 0) {
        this.ordinis = [];
        this.loadMore = false;
        return;
      }
      if (!add) {
        this.ordinis = _result.result;
      } else {
        _result.result.forEach((ord: Ordine) => this.ordinis.push(ord));
      }
      this.applyPendingOrdineDeletes();
      this.loadMore = _result.totalCount > this.ordinis.length;
    } catch {
      if (token !== this.requestToken) {
        return;
      }
      this.saveMessage.messageError("Ci sono problemi con le API ArchiCONNECT.");
    } finally {
      if (token === this.requestToken) {
        this.loading = false;
      }
    }
  }

  sumQta = () => {
    let totalQta = 0;
    this.ordinis.forEach((ordine: Ordine) => totalQta += ordine.quantita);
    this.totalQtaChange.emit(totalQta);
  };

  excludeOrdine = (ordine: Ordine) => {
    this.ordinis = this.ordinis.filter((_ordine: Ordine) =>
      !(ordine.idDocumento === _ordine.idDocumento &&
        ordine.idRiga === _ordine.idRiga &&
        ordine.idRigaLavorazione === _ordine.idRigaLavorazione));
    this.totalQtaChange.emit(this.ordinis.length);
  };

  selectOrdine = (ordine: Ordine) => {
    this.ordineSended.emit(ordine);
  };

  openHubMonitor(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.hubMonitorService.requestOpenHubMonitor();
  }

  ngOnDestroy(): void {
    this.autoLoadTriggerObserver.disconnect();
    this.subscription.unsubscribe();
  }

  private shouldIncludeUpdatedOrdine(ordine: Ordine): boolean {
    if (this.hasSearch()) {
      return false;
    }

    if ((this.filtroOrdini === 1 || this.filtroOrdini === 3) &&
      !this.risorseService.myRisorse.value.some((_risorsa) => _risorsa.id === ordine.risorsaId)) {
      return false;
    }

    if ((this.filtroOrdini === 2 || this.filtroOrdini === 3) &&
      !this.lavorazioniService.myLavorazioni.value.some((_lavorazione) => _lavorazione.id === ordine.lavorazioneId)) {
      return false;
    }

    return matchesFilterItems(ordine, this.localFilters);
  }

  private hasSearch(): boolean {
    return Boolean(this.localService.searchText.getValue()?.toString().trim());
  }

  private observeAutoLoadTrigger(): void {
    this.autoLoadTriggerObserver.observe(this.autoLoadTriggerElement);
  }

  private applyPendingOrdineDeletes(): void {
    this.ordinis.forEach((_ordine: Ordine) => {
      if (this.pendingOrdineDeletes.some((_pending) => this.isSameOrdine(_pending, _ordine))) {
        _ordine.onQueue = true;
      }
    });
  }

  private isSameOrdine(left: Ordine, right: Ordine): boolean {
    return left?.organizzazioneId === right?.organizzazioneId &&
      left?.idDocumento === right?.idDocumento &&
      left?.idRiga === right?.idRiga &&
      left?.idRigaLavorazione === right?.idRigaLavorazione;
  }
}
